/**
 * TractorBeamBehavior.ts
 *
 * Behavior attached to Player that manages:
 * - Entity detection within the beam bounds
 * - Abduction coordination with AbductableBehavior
 * - All particle effects (burn marks + rising lines)
 *
 * Flow:
 * 1. Player presses Z -> tractorBeamStarted event
 * 2. TractorBeam activates visually
 * 3. TractorBeamBehavior enables detection in update()
 * 4. Entity enters beam -> startAbduction()
 * 5. AbductableBehavior.update() moves entity upward
 * 6. Entity collides with player -> completeAbduction() -> entityAbducted
 * 7. Game.ts adds bioMatter, entity is destroyed
 *
 * Cancellation:
 * - Entity exits beam -> cancelAbduction() -> falls
 * - Beam deactivates -> cancelAllAbductions() -> all fall
 */

import Phaser from 'phaser';
import { Behavior, BehaviorConfig } from './Behavior';
import type { Entity } from '../base/Entity';
import type { TractorBeam } from '../TractorBeam';
import type { Player } from '../Player';
import { AbductableBehavior } from './AbductableBehavior';
import { ParticleRegistry } from '../../systems/ParticleRegistry';
import { LayerDepths } from '../../constants/enums';
import type { CollisionSystem } from '../../systems/CollisionSystem';

export interface TractorBeamBehaviorConfig extends BehaviorConfig {
  /** The TractorBeam visual sprite */
  tractorBeam: TractorBeam;
  /** Group containing all abductable entities */
  abductableGroup: Phaser.GameObjects.Group;
}

/**
 * TractorBeamBehavior - Manages entity detection and abduction within the beam
 */
export class TractorBeamBehavior extends Behavior {
  private tractorBeam: TractorBeam;
  private abductableGroup: Phaser.GameObjects.Group;

  /** Entities currently being abducted */
  private abductingEntities: Set<Entity> = new Set();

  /** Rising line particles for entities being abducted */
  private entityLines: Map<Entity, { timer: number; lines: Phaser.GameObjects.Rectangle[] }> = new Map();

  /** Burn mark particles at beam ground contact */
  private burnMarks: Phaser.GameObjects.Sprite[] = [];
  private burnMarkTimer: number = 0;

  // Particle config (loaded from JSON)
  private lineSpawnInterval: number;
  private lineRiseSpeed: number;
  private lineLifespan: number;
  private burnMarkInterval: number;
  private maxBurnMarks: number;

  /** Overlap collider for abduction completion */
  private playerOverlap: Phaser.Physics.Arcade.Collider | null = null;

  constructor(entity: Entity, config: TractorBeamBehaviorConfig) {
    super(entity, config);

    this.tractorBeam = config.tractorBeam;
    this.abductableGroup = config.abductableGroup;

    // Load particle config from tractor definition
    const particles = this.tractorBeam.getDefinition().particles;
    if (!particles) {
      console.error('[TractorBeamBehavior] Missing particles config in tractor definition', {
        tractorId: this.tractorBeam.getDefinition().id,
      });
      // Set defaults to prevent crashes, but this is a data error
      this.lineSpawnInterval = 100;
      this.lineRiseSpeed = 80;
      this.lineLifespan = 600;
      this.burnMarkInterval = 80;
      this.maxBurnMarks = 20;
    } else {
      this.lineSpawnInterval = particles.lineSpawnInterval;
      this.lineRiseSpeed = particles.lineRiseSpeed;
      this.lineLifespan = particles.lineLifespan;
      this.burnMarkInterval = particles.burnMarkInterval;
      this.maxBurnMarks = particles.maxBurnMarks;
    }

    // Setup overlap collider between player and entities for abduction completion
    this.setupPlayerOverlap();

    // Listen for beam events
    this.scene.events.on('tractorBeamStarted', this.onBeamStarted, this);
    this.scene.events.on('tractorBeamStopped', this.onBeamStopped, this);

    // Listen for entity destruction to cleanup tracking
    this.scene.events.on('entityDied', this.onEntityDied, this);

    // Cleanup on scene shutdown
    this.scene.events.once('shutdown', this.destroy, this);
    this.scene.events.once('destroy', this.destroy, this);
  }

  /**
   * Setup overlap collider between player and abductable entities
   */
  private setupPlayerOverlap(): void {
    this.playerOverlap = this.scene.physics.add.overlap(
      this.entity,
      this.abductableGroup,
      this.onPlayerEntityOverlap as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
      undefined,
      this
    );
  }

  /**
   * Handle collision between player and entity
   */
  private onPlayerEntityOverlap(
    _playerObj: Phaser.GameObjects.GameObject,
    entityObj: Phaser.GameObjects.GameObject
  ): void {
    const entity = entityObj as Entity;
    if (!entity.active) return;

    const abductable = this.getAbductableBehavior(entity);
    if (!abductable) return;

    // Only complete if entity is being abducted (not just walking by)
    if (abductable.isBeingAbducted && this.abductingEntities.has(entity)) {
      abductable.completeAbduction();
      this.abductingEntities.delete(entity);
      // Remove particles when entity is eaten
      this.destroyEntityParticles(entity);
    }
  }

  /**
   * Handle beam activation
   */
  private onBeamStarted(): void {
    const player = this.entity as Player;
    // Start player eating animation
    player.startAbducting();
    // Reset burn mark timer
    this.burnMarkTimer = 0;
  }

  /**
   * Handle beam deactivation - cancel all active abductions
   */
  private onBeamStopped(): void {
    const player = this.entity as Player;
    // Stop player eating animation
    player.stopAbducting();
    this.cancelAllAbductions();
  }

  /**
   * Handle entity death - keep abduction going if in progress
   */
  private onEntityDied(data: { entity: Entity }): void {
    const abductable = this.getAbductableBehavior(data.entity);

    // If being abducted, keep in tracking - behavior will continue
    if (abductable?.isBeingAbducted) {
      return;
    }

    // Otherwise, remove from tracking
    this.abductingEntities.delete(data.entity);
    this.destroyEntityParticles(data.entity);
  }

  /**
   * Update - check for entities in beam and manage abductions
   */
  update(time: number, delta: number): void {
    if (!this.tractorBeam.getIsActive()) {
      // Beam is not active - ensure no entities are being tracked
      if (this.abductingEntities.size > 0) {
        this.cancelAllAbductions();
      }
      return;
    }

    // Get beam bounds
    const bounds = this.tractorBeam.getBeamBounds();
    if (bounds.width === 0 || bounds.height === 0) return;

    const tractorPower = this.tractorBeam.getDefinition().tractorPower;

    // Check all entities in the abductable group
    const entities = this.abductableGroup.getChildren() as Entity[];

    for (const entity of entities) {
      if (!entity.active) continue;

      const abductable = this.getAbductableBehavior(entity);
      if (!abductable) continue;

      const isInBeam = this.isEntityInBeam(entity, bounds);

      if (isInBeam) {
        // Entity is in beam
        if (abductable.canBeAbducted()) {
          // Start abduction
          abductable.startAbduction(tractorPower);
          this.abductingEntities.add(entity);
          // Create particles around entity
          this.createEntityParticles(entity);
        } else if (!abductable.isBeingAbducted) {
          // Debug: Why can't this entity be abducted?
          console.log(`[TractorBeamBehavior] Entity in beam but canBeAbducted=false, isBeingAbducted=${abductable.isBeingAbducted}`);
        }
        // Completion is handled by physics overlap with player
      } else {
        // Entity is NOT in beam
        if (abductable.isBeingAbducted && this.abductingEntities.has(entity)) {
          // Entity was being abducted but left the beam - cancel
          abductable.cancelAbduction();
          this.abductingEntities.delete(entity);
          // Remove particles
          this.destroyEntityParticles(entity);
        }
      }
    }

    // Update rising line particles
    this.updateEntityParticles(delta);

    // Update burn marks
    this.updateBurnMarks(delta);
  }

  /**
   * Check if entity is within beam bounds
   */
  private isEntityInBeam(
    entity: Entity,
    bounds: { x: number; y: number; width: number; height: number }
  ): boolean {
    // Get entity bounds
    const entityX = entity.x;
    const entityY = entity.y;
    const entityHalfWidth = entity.displayWidth * 0.5;
    const entityHalfHeight = entity.displayHeight * 0.5;

    const entityLeft = entityX - entityHalfWidth;
    const entityRight = entityX + entityHalfWidth;
    const entityTop = entityY - entityHalfHeight;
    const entityBottom = entityY + entityHalfHeight;

    // Check overlap with beam bounds
    const beamLeft = bounds.x;
    const beamRight = bounds.x + bounds.width;
    const beamTop = bounds.y;
    const beamBottom = bounds.y + bounds.height;

    // Check if entity center X is within beam width
    const horizontalOverlap = entityX >= beamLeft && entityX <= beamRight;

    // Check if entity top is within beam height (entity is below beam top and above beam bottom)
    const verticalOverlap = entityTop <= beamBottom && entityBottom >= beamTop;

    return horizontalOverlap && verticalOverlap;
  }

  /**
   * Get AbductableBehavior from an entity
   */
  private getAbductableBehavior(entity: Entity): AbductableBehavior | null {
    return entity.getBehavior(AbductableBehavior) ?? null;
  }

  /**
   * Cancel all active abductions
   */
  private cancelAllAbductions(): void {
    for (const entity of this.abductingEntities) {
      if (!entity.active) continue;

      const abductable = this.getAbductableBehavior(entity);
      if (abductable?.isBeingAbducted) {
        abductable.cancelAbduction();
      }
      // Remove particles
      this.destroyEntityParticles(entity);
    }

    this.abductingEntities.clear();
  }

  // ========== Rising Line Particles ==========

  /**
   * Initialize line tracking for an entity being abducted
   */
  private createEntityParticles(entity: Entity): void {
    if (this.entityLines.has(entity)) return;

    console.log(`[TractorBeamBehavior] Creating line particles for entity at (${entity.x}, ${entity.y})`);

    // Initialize tracking for this entity
    this.entityLines.set(entity, { timer: 0, lines: [] });
  }

  /**
   * Spawn a vertical rising line near an entity
   */
  private spawnRisingLine(entity: Entity): void {
    const data = this.entityLines.get(entity);
    if (!data) return;

    // Random X position within entity width
    const halfWidth = entity.displayWidth * 0.4;
    const x = entity.x + (Math.random() - 0.5) * halfWidth * 2;
    // Start from bottom of entity
    const y = entity.y + entity.displayHeight * 0.3;

    // Create vertical line (2-3px wide, 8-12px tall)
    const width = 2 + Math.random();
    const height = 8 + Math.random() * 4;

    // Get particle color from current tractor definition
    const particleTint = this.tractorBeam.getDefinition().particleTint;

    const line = this.scene.add.rectangle(x, y, width, height, particleTint, 1);
    line.setBlendMode(Phaser.BlendModes.ADD);
    line.setDepth(LayerDepths.TractorLine);
    line.setData('startTime', this.scene.time.now);
    line.setData('startY', y);

    data.lines.push(line);
    ParticleRegistry.add('risingLines');
  }

  /**
   * Destroy all lines for an entity
   */
  private destroyEntityParticles(entity: Entity): void {
    const data = this.entityLines.get(entity);
    if (data) {
      ParticleRegistry.remove('risingLines', data.lines.length);
      for (const line of data.lines) {
        line.destroy();
      }
      this.entityLines.delete(entity);
    }
  }

  /**
   * Update rising lines - spawn new ones and animate existing
   */
  private updateEntityParticles(delta: number): void {
    const now = this.scene.time.now;

    for (const [entity, data] of this.entityLines) {
      if (!entity.active) {
        this.destroyEntityParticles(entity);
        continue;
      }

      // Spawn new lines periodically
      data.timer += delta;
      if (data.timer >= this.lineSpawnInterval) {
        data.timer = 0;
        this.spawnRisingLine(entity);
        this.spawnRisingLine(entity); // Spawn 2 lines per interval
      }

      // Update existing lines
      const linesToRemove: Phaser.GameObjects.Rectangle[] = [];

      for (const line of data.lines) {
        const startTime = line.getData('startTime') as number;
        const startY = line.getData('startY') as number;
        const age = now - startTime;
        const progress = age / this.lineLifespan;

        if (progress >= 1) {
          linesToRemove.push(line);
          continue;
        }

        // Move upward
        const riseDistance = (age / 1000) * this.lineRiseSpeed;
        line.y = startY - riseDistance;

        // Follow entity X position (with slight offset from spawn position)
        const originalX = line.x;
        const entityDeltaX = entity.x - (line.getData('entityStartX') ?? entity.x);
        if (!line.getData('entityStartX')) {
          line.setData('entityStartX', entity.x);
        }
        line.x = originalX + entityDeltaX * 0.5;

        // Fade out
        line.setAlpha(1 - progress * 0.8);

        // Slight color shift to white as it rises
        const colorProgress = Math.min(1, progress * 1.5);
        const r = Math.floor(0x00 + (0xff - 0x00) * colorProgress);
        const g = Math.floor(0xff);
        const b = Math.floor(0xff);
        line.setFillStyle(Phaser.Display.Color.GetColor(r, g, b), line.alpha);
      }

      // Remove expired lines
      for (const line of linesToRemove) {
        const idx = data.lines.indexOf(line);
        if (idx > -1) {
          data.lines.splice(idx, 1);
        }
        line.destroy();
        ParticleRegistry.remove('risingLines');
      }
    }
  }

  // ========== Burn Mark Particles ==========

  /**
   * Update burn marks - create them when beam touches ground
   */
  private updateBurnMarks(delta: number): void {
    if (!this.tractorBeam.getIsActive()) return;

    const def = this.tractorBeam.getDefinition();
    const beamBounds = this.tractorBeam.getBeamBounds();
    const beamBottomY = beamBounds.y + beamBounds.height;

    // Get the solid Y position to check if beam is touching ground
    const collisionSystem = this.scene.registry.get('collisionSystem') as CollisionSystem | undefined;
    if (!collisionSystem) {
      console.error('[TractorBeamBehavior] CollisionSystem not found in registry');
      return;
    }

    const solidY = collisionSystem.getSolidYBelow(this.entity.x, beamBounds.y);
    if (solidY === null) return;

    // Check if beam is touching the solid (not blocked by an entity above it)
    const solidThreshold = solidY - 5; // Small tolerance
    const isTouchingSolid = beamBottomY >= solidThreshold;

    if (!isTouchingSolid) return;

    // Create burn marks periodically
    this.burnMarkTimer += delta;
    if (this.burnMarkTimer >= this.burnMarkInterval) {
      this.burnMarkTimer = 0;

      // Create burn mark at random position within beam width
      const randomX = this.entity.x + (Math.random() - 0.5) * beamBounds.width;
      this.createBurnMark(randomX, solidY - 5);
    }
  }

  /**
   * Create a burn mark sprite at the given position
   */
  private createBurnMark(x: number, y: number): void {
    // Safety limit
    if (this.burnMarks.length >= this.maxBurnMarks) {
      // Remove oldest burn mark
      const oldest = this.burnMarks.shift();
      if (oldest) {
        this.scene.tweens.killTweensOf(oldest);
        oldest.destroy();
        ParticleRegistry.remove('burnMarks');
      }
    }

    // Create burn mark sprite - bigger size
    const burnMark = this.scene.add.sprite(x, y, 'adornobiomoney');
    burnMark.setScale(1.5);
    burnMark.setAlpha(0.9);
    // Use tractor's particle color
    burnMark.setTint(this.tractorBeam.getDefinition().particleTint);
    burnMark.setBlendMode(Phaser.BlendModes.ADD);
    burnMark.setDepth(LayerDepths.TractorBurnMark);
    burnMark.setRotation(Math.random() * Math.PI * 2);

    this.burnMarks.push(burnMark);
    ParticleRegistry.add('burnMarks');

    // Fade out and destroy after 2 seconds
    this.scene.tweens.add({
      targets: burnMark,
      alpha: 0,
      scale: 0.4,
      duration: 2000,
      ease: 'Power2',
      onComplete: () => {
        const index = this.burnMarks.indexOf(burnMark);
        if (index > -1) {
          this.burnMarks.splice(index, 1);
        }
        burnMark.destroy();
        ParticleRegistry.remove('burnMarks');
      },
    });
  }

  // ========== Debug Methods ==========

  /**
   * Get number of entities currently being abducted
   */
  getAbductingCount(): number {
    return this.abductingEntities.size;
  }

  /**
   * Get number of active rising lines
   */
  getEntityParticleCount(): number {
    let count = 0;
    for (const data of this.entityLines.values()) {
      count += data.lines.length;
    }
    return count;
  }

  /**
   * Get active burn mark count
   */
  getBurnMarkCount(): number {
    return this.burnMarks.length;
  }

  // ========== Cleanup ==========

  /**
   * Cleanup
   */
  destroy(): void {
    // Prevent double destroy
    if (this.isDestroyed) return;
    this.isDestroyed = true;

    this.scene.events.off('tractorBeamStarted', this.onBeamStarted, this);
    this.scene.events.off('tractorBeamStopped', this.onBeamStopped, this);
    this.scene.events.off('entityDied', this.onEntityDied, this);

    // Destroy overlap collider
    if (this.playerOverlap && this.playerOverlap.active !== undefined) {
      this.playerOverlap.destroy();
      this.playerOverlap = null;
    }

    // Destroy all rising lines (check if still valid)
    for (const data of this.entityLines.values()) {
      for (const line of data.lines) {
        if (line && line.scene) {
          line.destroy();
        }
      }
    }
    this.entityLines.clear();

    // Destroy all burn marks (check if still valid)
    for (const burnMark of this.burnMarks) {
      if (burnMark && burnMark.scene) {
        this.scene.tweens?.killTweensOf(burnMark);
        burnMark.destroy();
      }
    }
    this.burnMarks = [];

    this.cancelAllAbductions();
    this.abductingEntities.clear();

    super.destroy();
  }

  private isDestroyed = false;
}
